import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'
import FieldHelp from '../../components/FieldHelp'

interface Supplier {
  id: string
  name: string
  supplierCode?: string
  companyName?: string
  email?: string
  phone?: string
  address?: string
  taxId?: string
  paymentTerms?: string
  currency?: string
}

interface PurchaseOrder {
  id: string
  orderNumber: string
  orderDate: string
  expectedDate?: string
  supplier: string
  supplierId?: string
  supplierAddress?: string
  supplierTaxId?: string
  status: string
  subtotal: string
  taxAmount: string
  total: string
  currency?: string
  notes?: string
  terms?: string
  paymentTerms?: string
  items?: PurchaseOrderItemDetail[]
  purchaseInvoices?: Array<{
    id: string
    invoiceNumber: string
    invoiceDate: string
    total: string
    status: string
  }>
}

interface PurchaseOrderItemDetail {
  id: string
  description: string
  quantity: string
  unitPrice: string
  receivedQuantity: string
  taxRateId?: string
  accountId?: string
  costCenterId?: string
  taxRate?: {
    name: string
    rate: string
  }
  account?: {
    code: string
    name: string
  }
  costCenter?: {
    name: string
  }
}

interface Account {
  id: string
  code: string
  name: string
  rootType: string
}

interface TaxRate {
  id: string
  name: string
  rate: string
  taxType: string
}

interface CostCenter {
  id: string
  name: string
}

interface PurchaseOrderItem {
  description: string
  quantity: string
  unitPrice: string
  accountId: string
  taxRateId: string
  costCenterId: string
}

const PurchaseOrders: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission, loading: permissionsLoading } = usePermissions(user)
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([])
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [taxRates, setTaxRates] = useState<TaxRate[]>([])
  const [costCenters, setCostCenters] = useState<CostCenter[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(null)
  const [formData, setFormData] = useState({
    orderDate: new Date().toISOString().split('T')[0],
    expectedDate: '',
    supplier: '',
    supplierId: '',
    supplierAddress: '',
    supplierTaxId: '',
    currency: 'GBP',
    notes: '',
    terms: '',
    paymentTerms: '',
    items: [
      { description: '', quantity: '', unitPrice: '', accountId: '', taxRateId: '', costCenterId: '' },
    ] as PurchaseOrderItem[],
  })

  useEffect(() => {
    fetchPurchaseOrders()
    fetchSuppliers()
    fetchAccounts()
    fetchTaxRates()
    fetchCostCenters()
  }, [])

  const fetchPurchaseOrders = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/api/erp/purchase-orders')
      setPurchaseOrders(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching purchase orders:', error)
      toast.error('Failed to load purchase orders')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get('/api/erp/suppliers', {
        params: { includeInactive: 'false' },
      })
      setSuppliers(response.data.data || [])
    } catch (error) {
      console.error('Error fetching suppliers:', error)
    }
  }

  const fetchAccounts = async () => {
    try {
      const response = await axios.get('/api/erp/accounts', { params: { isActive: 'true', isGroup: 'false' } })
      const filtered = (response.data.data || []).filter((acc: Account) => 
        acc.rootType === 'EXPENSE' || acc.rootType === 'ASSET'
      )
      setAccounts(filtered)
    } catch (error) {
      console.error('Error fetching accounts:', error)
    }
  }

  const fetchTaxRates = async () => {
    try {
      const response = await axios.get('/api/erp/tax-rates')
      setTaxRates(response.data.data || [])
    } catch (error) {
      console.error('Error fetching tax rates:', error)
    }
  }

  const fetchCostCenters = async () => {
    try {
      const response = await axios.get('/api/erp/cost-centers')
      setCostCenters(response.data.data || [])
    } catch (error) {
      console.error('Error fetching cost centers:', error)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSupplierChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const supplierId = e.target.value
    const supplier = suppliers.find(s => s.id === supplierId)
    
    if (supplier) {
      setFormData(prev => ({
        ...prev,
        supplierId: supplier.id,
        supplier: supplier.name,
        supplierAddress: supplier.address || '',
        supplierTaxId: supplier.taxId || '',
        currency: supplier.currency || 'GBP',
        paymentTerms: supplier.paymentTerms || '',
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        supplierId: '',
        supplier: '',
        supplierAddress: '',
        supplierTaxId: '',
      }))
    }
  }

  const handleItemChange = (index: number, field: keyof PurchaseOrderItem, value: string) => {
    const newItems = [...formData.items]
    newItems[index] = { ...newItems[index], [field]: value }
    setFormData(prev => ({ ...prev, items: newItems }))
  }

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: '', unitPrice: '', accountId: '', taxRateId: '', costCenterId: '' }],
    }))
  }

  const removeItem = (index: number) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index),
      }))
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.supplier) {
      toast.error('Please enter supplier name')
      return
    }

    if (!formData.items || formData.items.length === 0 || formData.items.some(item => !item.description || !item.accountId)) {
      toast.error('Please fill in all required item fields')
      return
    }

    try {
      const payload: any = {
        orderDate: formData.orderDate,
        supplier: formData.supplier,
        supplierAddress: formData.supplierAddress || undefined,
        supplierTaxId: formData.supplierTaxId || undefined,
        currency: formData.currency,
        notes: formData.notes || undefined,
        terms: formData.terms || undefined,
        paymentTerms: formData.paymentTerms || undefined,
        items: formData.items.map(item => ({
          description: item.description,
          quantity: parseFloat(String(item.quantity || '0')) || 0,
          unitPrice: parseFloat(String(item.unitPrice || '0')) || 0,
          accountId: item.accountId,
          taxRateId: item.taxRateId || undefined,
          costCenterId: item.costCenterId || undefined,
        })),
      }

      if (formData.expectedDate) {
        payload.expectedDate = formData.expectedDate
      }

      if (formData.supplierId) {
        payload.supplierId = formData.supplierId
      }

      await axios.post('/api/erp/purchase-orders', payload)
      toast.success('Purchase order created successfully!')
      setShowCreateModal(false)
      setFormData({
        orderDate: new Date().toISOString().split('T')[0],
        expectedDate: '',
        supplier: '',
        supplierId: '',
        supplierAddress: '',
        supplierTaxId: '',
        currency: 'GBP',
        notes: '',
        terms: '',
        paymentTerms: '',
        items: [
          { description: '', quantity: '', unitPrice: '', accountId: '', taxRateId: '', costCenterId: '' },
        ],
      })
      fetchPurchaseOrders()
    } catch (error: any) {
      console.error('Error creating purchase order:', error)
      toast.error(error.response?.data?.error || 'Failed to create purchase order')
    }
  }

  const handleView = async (id: string) => {
    try {
      const response = await axios.get(`/api/erp/purchase-orders/${id}`)
      setSelectedOrder(response.data.data)
      setShowViewModal(true)
    } catch (error: any) {
      console.error('Error fetching purchase order:', error)
      toast.error('Failed to load purchase order')
    }
  }

  const handleSubmit = async (id: string) => {
    try {
      await axios.post(`/api/erp/purchase-orders/${id}/submit`)
      toast.success('Purchase order submitted for approval')
      fetchPurchaseOrders()
    } catch (error: any) {
      console.error('Error submitting purchase order:', error)
      toast.error(error.response?.data?.error || 'Failed to submit purchase order')
    }
  }

  const handleApprove = async (id: string) => {
    try {
      await axios.post(`/api/erp/purchase-orders/${id}/approve`)
      toast.success('Purchase order approved')
      fetchPurchaseOrders()
    } catch (error: any) {
      console.error('Error approving purchase order:', error)
      toast.error(error.response?.data?.error || 'Failed to approve purchase order')
    }
  }

  const handleOrder = async (id: string) => {
    try {
      await axios.post(`/api/erp/purchase-orders/${id}/order`)
      toast.success('Purchase order marked as ordered')
      fetchPurchaseOrders()
    } catch (error: any) {
      console.error('Error ordering purchase order:', error)
      toast.error(error.response?.data?.error || 'Failed to mark purchase order as ordered')
    }
  }

  const handleCancel = async (id: string) => {
    if (!confirm('Are you sure you want to cancel this purchase order?')) {
      return
    }

    try {
      await axios.post(`/api/erp/purchase-orders/${id}/cancel`)
      toast.success('Purchase order cancelled')
      fetchPurchaseOrders()
    } catch (error: any) {
      console.error('Error cancelling purchase order:', error)
      toast.error(error.response?.data?.error || 'Failed to cancel purchase order')
    }
  }

  const handleCreateInvoice = async (id: string) => {
    try {
      const response = await axios.post(`/api/erp/purchase-orders/${id}/create-invoice`, {
        invoiceDate: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        postingDate: new Date().toISOString().split('T')[0],
      })
      toast.success('Purchase invoice created from purchase order!')
      setShowViewModal(false)
      // Navigate to purchase invoice or show it
      window.location.href = `/erp/purchase-invoices`
    } catch (error: any) {
      console.error('Error creating purchase invoice:', error)
      toast.error(error.response?.data?.error || 'Failed to create purchase invoice from purchase order')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800'
      case 'SUBMITTED':
        return 'bg-yellow-100 text-yellow-800'
      case 'APPROVED':
        return 'bg-blue-100 text-blue-800'
      case 'ORDERED':
        return 'bg-purple-100 text-purple-800'
      case 'PARTIALLY_RECEIVED':
        return 'bg-orange-100 text-orange-800'
      case 'RECEIVED':
        return 'bg-green-100 text-green-800'
      case 'CANCELLED':
        return 'bg-red-100 text-red-800'
      case 'CLOSED':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (permissionsLoading) {
    return <div className="p-6">Loading permissions...</div>
  }

  if (!hasPermission('erp_purchase_invoice', 'read')) {
    return <div className="p-6">You don't have permission to view purchase orders.</div>
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <h1 className="text-2xl font-bold text-gray-900">Purchase Orders</h1>
          <PageHelp pageIdentifier="purchase-orders" title="Purchase Orders" />
        </div>
        {hasPermission('erp_purchase_invoice', 'write') && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create Purchase Order
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading purchase orders...</p>
        </div>
      ) : purchaseOrders.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-600">No purchase orders found.</p>
          {hasPermission('erp_purchase_invoice', 'write') && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create Your First Purchase Order
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Supplier</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expected Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {purchaseOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{order.orderNumber}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{order.supplier}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{new Date(order.orderDate).toLocaleDateString()}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{order.expectedDate ? new Date(order.expectedDate).toLocaleDateString() : '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {parseFloat(order.total).toLocaleString('en-GB', { style: 'currency', currency: order.currency || 'GBP' })}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(order.status)}`}>
                      {order.status.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleView(order.id)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      View
                    </button>
                    {order.status === 'DRAFT' && hasPermission('erp_purchase_invoice', 'write') && (
                      <button
                        onClick={() => handleSubmit(order.id)}
                        className="text-yellow-600 hover:text-yellow-900 mr-4"
                      >
                        Submit
                      </button>
                    )}
                    {order.status === 'SUBMITTED' && hasPermission('erp_purchase_invoice', 'approve') && (
                      <button
                        onClick={() => handleApprove(order.id)}
                        className="text-green-600 hover:text-green-900 mr-4"
                      >
                        Approve
                      </button>
                    )}
                    {order.status === 'APPROVED' && hasPermission('erp_purchase_invoice', 'write') && (
                      <>
                        <button
                          onClick={() => handleOrder(order.id)}
                          className="text-purple-600 hover:text-purple-900 mr-4"
                        >
                          Mark as Ordered
                        </button>
                        <button
                          onClick={() => handleCreateInvoice(order.id)}
                          className="text-indigo-600 hover:text-indigo-900 mr-4"
                        >
                          Create Invoice
                        </button>
                      </>
                    )}
                    {(order.status === 'ORDERED' || order.status === 'PARTIALLY_RECEIVED') && hasPermission('erp_purchase_invoice', 'write') && (
                      <button
                        onClick={() => handleCreateInvoice(order.id)}
                        className="text-indigo-600 hover:text-indigo-900 mr-4"
                      >
                        Create Invoice
                      </button>
                    )}
                    {order.status !== 'CANCELLED' && order.status !== 'CLOSED' && hasPermission('erp_purchase_invoice', 'write') && (
                      <button
                        onClick={() => handleCancel(order.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Create Purchase Order</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreate}>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Order Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="orderDate"
                      value={formData.orderDate}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expected Date
                      <FieldHelp content="The expected delivery date from the supplier. This helps with planning and tracking." />
                    </label>
                    <input
                      type="date"
                      name="expectedDate"
                      value={formData.expectedDate}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Supplier <span className="text-red-500">*</span>
                    <FieldHelp content="Select a supplier from your supplier list, or enter a new supplier name manually. If you select a supplier, their details will be auto-filled." />
                  </label>
                  <div className="mt-1 flex gap-2">
                    <select
                      name="supplierId"
                      value={formData.supplierId}
                      onChange={handleSupplierChange}
                      className="flex-1 block border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value="">-- Select Supplier or Enter Manually --</option>
                      {suppliers.map((supplier) => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.supplierCode ? `${supplier.supplierCode} - ` : ''}{supplier.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <input
                    type="text"
                    name="supplier"
                    value={formData.supplier}
                    onChange={handleInputChange}
                    required
                    className="mt-2 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Supplier name (required)"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Supplier Address
                    </label>
                    <input
                      type="text"
                      name="supplierAddress"
                      value={formData.supplierAddress}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Supplier Tax ID
                    </label>
                    <input
                      type="text"
                      name="supplierTaxId"
                      value={formData.supplierTaxId}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Currency
                  </label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="GBP">GBP (£)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="CAD">CAD (C$)</option>
                    <option value="AUD">AUD (A$)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Items <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-3">
                    {formData.items.map((item, index) => (
                      <div key={index} className="p-4 border border-gray-200 rounded-lg">
                        <div className="grid grid-cols-12 gap-2">
                          <div className="col-span-12">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Description *</label>
                            <input
                              type="text"
                              value={item.description}
                              onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                              required
                            />
                          </div>
                          <div className="col-span-4">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Quantity *</label>
                            <input
                              type="number"
                              step="0.01"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                              required
                            />
                          </div>
                          <div className="col-span-4">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Unit Price *</label>
                            <input
                              type="number"
                              step="0.01"
                              value={item.unitPrice}
                              onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                              required
                            />
                          </div>
                          <div className="col-span-4">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Account *</label>
                            <select
                              value={item.accountId}
                              onChange={(e) => handleItemChange(index, 'accountId', e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                              required
                            >
                              <option value="">Select Account</option>
                              {accounts.map((acc) => (
                                <option key={acc.id} value={acc.id}>
                                  {acc.code} - {acc.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-span-4">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Tax Rate</label>
                            <select
                              value={item.taxRateId}
                              onChange={(e) => handleItemChange(index, 'taxRateId', e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                            >
                              <option value="">No Tax</option>
                              {taxRates.map((tr) => (
                                <option key={tr.id} value={tr.id}>
                                  {tr.name} ({tr.rate}%)
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-span-4">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Cost Center</label>
                            <select
                              value={item.costCenterId}
                              onChange={(e) => handleItemChange(index, 'costCenterId', e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                            >
                              <option value="">No Cost Center</option>
                              {costCenters.map((cc) => (
                                <option key={cc.id} value={cc.id}>
                                  {cc.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-span-4 flex items-end">
                            {formData.items.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeItem(index)}
                                className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addItem}
                      className="px-4 py-2 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
                    >
                      + Add Item
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    rows={3}
                    placeholder="Additional notes"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Create Purchase Order
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{selectedOrder.orderNumber}</h2>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Supplier</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedOrder.supplier}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Status</label>
                  <p className="mt-1">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(selectedOrder.status)}`}>
                      {selectedOrder.status.replace(/_/g, ' ')}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Order Date</label>
                  <p className="mt-1 text-sm text-gray-900">{new Date(selectedOrder.orderDate).toLocaleDateString()}</p>
                </div>
                {selectedOrder.expectedDate && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Expected Date</label>
                    <p className="mt-1 text-sm text-gray-900">{new Date(selectedOrder.expectedDate).toLocaleDateString()}</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-500">Total</label>
                  <p className="mt-1 text-sm font-medium text-gray-900">
                    {parseFloat(selectedOrder.total).toLocaleString('en-GB', { style: 'currency', currency: selectedOrder.currency || 'GBP' })}
                  </p>
                </div>
              </div>

              {selectedOrder.items && selectedOrder.items.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">Items</label>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Received</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedOrder.items.map((item) => (
                          <tr key={item.id}>
                            <td className="px-4 py-2 text-sm text-gray-900">{item.description}</td>
                            <td className="px-4 py-2 text-sm text-gray-500">{parseFloat(item.quantity).toFixed(2)}</td>
                            <td className="px-4 py-2 text-sm text-gray-500">{parseFloat(item.receivedQuantity).toFixed(2)}</td>
                            <td className="px-4 py-2 text-sm text-gray-500">
                              {parseFloat(item.unitPrice).toLocaleString('en-GB', { style: 'currency', currency: selectedOrder.currency || 'GBP' })}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {parseFloat(item.total).toLocaleString('en-GB', { style: 'currency', currency: selectedOrder.currency || 'GBP' })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {selectedOrder.purchaseInvoices && selectedOrder.purchaseInvoices.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">Purchase Invoices</label>
                  <div className="space-y-2">
                    {selectedOrder.purchaseInvoices.map((invoice) => (
                      <div key={invoice.id} className="p-3 bg-gray-50 rounded">
                        <div className="flex justify-between">
                          <span className="text-sm font-medium">{invoice.invoiceNumber}</span>
                          <span className="text-sm text-gray-500">{new Date(invoice.invoiceDate).toLocaleDateString()}</span>
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          {parseFloat(invoice.total).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })} - {invoice.status}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedOrder.notes && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Notes</label>
                  <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{selectedOrder.notes}</p>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              {(selectedOrder.status === 'APPROVED' || selectedOrder.status === 'ORDERED' || selectedOrder.status === 'PARTIALLY_RECEIVED') && hasPermission('erp_purchase_invoice', 'write') && (
                <button
                  onClick={() => handleCreateInvoice(selectedOrder.id)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Create Purchase Invoice
                </button>
              )}
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PurchaseOrders
